<script lang="ts" setup>
import type { Item, Takeaway } from '~/models';

import SaleItem from '../common/SaleItem.vue';
import SaleSummary from '../common/SaleSummary.vue';

import { useNuxtApp } from '#app';
import { useNotify } from '~/composables/useNotify';

import EmptyCartIcon from '~/components/icons/EmptyCartIcon.vue';

const { $device: { isMobile } } = useNuxtApp();
const { state, isCheckoutDisabled, goToCheckout } = useCart();

const cartStore = useCartStore();
const preferenceStore = usePreferenceStore();
const { emitEvent: emitGA4Event } = analyticsUtils();

const isLoading = ref<boolean>(false);

const collectionTime = computed<string>(() => {
  const time = preferenceStore.preference.general.estimatedCollectionTime || 15;
  return `${time} - ${time + 10} mins`;
});

const deliveryTime = computed<string>(() => {
  const time = preferenceStore.preference.general.estimatedDeliveryTime || 40;
  return `${time} - ${time + 10} mins`;
});

const setTakeaway = (takeawayType: Takeaway['type']) => {
  state.takeawayType = takeawayType;
  cartStore.setTakeawayType(takeawayType);
};

const formatItem = (item: Item): string => {
  return `${item.quantity}x ${item.name}`;
};

const onGoToCheckout = async (): Promise<void> => {
  try {
    const formattedItems = cartStore.items.map(formatItem).join(', ');
    emitGA4Event('click:checkout', { items_list: formattedItems });

    await goToCheckout();
  } catch (e) {
    useNotify().error((e as Error).message);
  }
};

const onRemoveItem = async (item: Item): Promise<void> => {
  try {
    isLoading.value = true;
    await cartStore.removeItem(item);
  } finally {
    isLoading.value = false;
  }
};

const onRemoveVoucher = (): void => {
  cartStore.setVoucher(null);
};
</script>

<template>
  <div class="cart h-fit p-2 overflow-hidden bg-card">
    <div
      v-if="!isMobile"
      class="flex items-center justify-center gap-2 border-b-2 py-1.5 border-company-1"
      data-test="header"
    >
      <UIcon
        name="ic:round-shopping-basket"
        class="text-4xl"
        data-test="header-icon"
      />

      <span
        class="font-semibold text-xl"
        data-test="header-title"
      >
        Your Order
      </span>
    </div>

    <div class="relative flex mb-2">
      <button
        type="button"
        class="flex flex-col items-center p-2 flex-1 outline-none disabled:opacity-50"
        :disabled="!preferenceStore.inDeliveryTime"
        data-test="takeaway-delivery"
        @click="setTakeaway('Delivery')"
      >
        <span class="font-semibold text-xl">Delivery</span>
        <span class="text-sm subtext-card">{{ deliveryTime }}</span>
      </button>

      <button
        type="button"
        class="flex flex-col items-center p-2 flex-1 outline-none disabled:opacity-50"
        :disabled="!preferenceStore.preference.general.enableCollection"
        data-test="takeaway-collection"
        @click="setTakeaway('Collection')"
      >
        <span class="font-semibold text-xl">Collection</span>
        <span class="text-sm subtext-card">{{ collectionTime }}</span>
      </button>

      <hr
        class="absolute w-1/2 bottom-0 border-t-2 border-company-1 transition-all"
        :class="{
          'left-0': state.takeawayType === 'Delivery',
          'left-1/2': state.takeawayType === 'Collection'
        }"
      >
    </div>

    <div class="flex flex-col">
      <EmptyCartIcon
        v-if="cartStore.items.length === 0"
        class="self-center my-10"
        data-test="empty-cart"
      />

      <template v-else>
        <SaleItem
          v-for="item of cartStore.items"
          :key="item.productUuid"
          :item="item"
          editable
          :is-loading="isLoading"
          @remove="() => onRemoveItem(item)"
        />

        <SaleSummary
          class="leading-8 border-current border-b mb-2"
          separator
          :subtotal="cartStore.subtotal"
          :delivery-fee="cartStore.deliveryFee"
          :discount-value="cartStore.discountSubTotal"
          :discount-perc="cartStore.discountSubTotalPercentage"
          :service-charge="cartStore.serviceCharge"
          :voucher-code="cartStore.voucher?.discount.voucherCode"
          :voucher-discount="cartStore.voucherDiscount"
          :voucher-valid="cartStore.voucher?.valid"
          :total="cartStore.total"
          @click:remove-voucher="onRemoveVoucher"
        />
      </template>
    </div>

    <div class="flex flex-col gap-2">
      <div>
        <UTextarea
          v-model="state.comment"
          :rows="1"
          autoresize
          size="xl"
          placeholder="Leave a comment"
          data-test="comment"
        />
        <span class="ml-3.5 text-xs subtext-card">
          e.g Delivery/Collection instructions, dietary restrictions
        </span>
      </div>

      <UButton
        v-if="!isMobile"
        block
        class="bg-company-1 font-semibold uppercase text-sm py-4"
        label="Checkout"
        :disabled="isCheckoutDisabled"
        data-test="checkout"
        @click="onGoToCheckout()"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
body.mobile {
  .cart {
    border-radius: 0;
    min-height: inherit;
  }
}

.cart {
  border-radius: 0.5rem;
}
</style>
